import { Stage, Layer } from 'react-konva';
import { useEffect, useRef, useState, forwardRef, useMemo } from 'react';
import Box from '@pages/ai-product/image-editor/Box';
import ProductImage from '@pages/ai-product/image-editor/ProductImage';
import ShadowImage from '@pages/ai-product/image-editor/ShadowImage';
import ColorBackground from '@pages/ai-product/image-editor/ColorBackground';

const VideoPlayer = forwardRef(({ src }, ref) => {
  return (
    <video
      ref={ ref }
      autoPlay
      playsInline
      loop
      muted
      controls
      style={ {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '4px',
      } }
    >
      <source src={ src } type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
});

function ProductImageEditor({ image, variant, mode, intensity, gamma, color = {
  r: 255,
  g: 255,
  b: 255,
  a: 1,
} }, stageRef) {
  const [rectangles, setRectangles] = useState([]);
  const [selectedId, selectShape] = useState(image.selectedBbox);
  const [isReady, setIsReady] = useState(false);
  const divRef = useRef(null);
  const imageRef = useRef();
  const shadowRef = useRef();
  const videoRef = useRef();
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const resizeObserver = useRef(null);

  useEffect(() => {
    // Error handler for ResizeObserver
    const handleResizeError = () => {
      if (divRef.current?.offsetHeight && divRef.current?.offsetWidth) {
        setDimensions({
          width: divRef.current.offsetWidth,
          height: divRef.current.offsetHeight,
        });
      }
    };

    resizeObserver.current = new ResizeObserver(entries => {
      try {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setDimensions({ width, height });
        }
      } catch (e) {
        handleResizeError();
      }
    });

    if (divRef.current) {
      resizeObserver.current.observe(divRef.current);
    }

    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      }
    };
  }, []);

  const checkDeselect = (e) => {
    const clickedOnEmpty = e.target === e.target.getStage() || e.target.className === 'Image';
    if (clickedOnEmpty) {
      selectShape(null);
      image.selectedBbox = null;
    }
  };

  const backgroundSrc = useMemo(() => {
    if (!image) return null;

    if (mode === 'VIDEO' && image.lifestyle?.[variant]?.videoSrc) {
      return image.lifestyle[variant].videoSrc;
    }
    if (mode === 'SILO' && !image.siloSrc) {
      return image.alphaSrc;
    }
    if (mode === 'SILO' && image.siloSrc) {
      return image.alphaSrc;
    }
    if (mode === 'BACKGROUND' && !image.lifestyle?.length) {
      return image.alphaSrc;
    }
    if (mode === 'BACKGROUND' && image.lifestyle?.length) {
      return image.lifestyle[variant].src;
    }
    return image.src;
  }, [image, variant, mode]);

  useEffect(() => {
    selectShape(image?.selectedBbox);
  }, [image]);

  useEffect(() => {
    setIsReady(false);
  }, [backgroundSrc]);

  if (mode === 'VIDEO' && backgroundSrc) {
    return (
      <div
        ref={ divRef }
        style={ {
          maxHeight: '75%',
          height: 'calc(100% - 5px)',
          width: 'calc(100% - 5px)',
        } }
      >
        <VideoPlayer src={ backgroundSrc } ref={ videoRef } />
      </div>
    );
  }

  return (
    <div
      ref={ divRef }
      style={ {
        maxHeight: '75%',
        height: 'calc(100% - 5px)',
        width: 'calc(100% - 5px)',
      } }
    >
      <Stage
        ref={ stageRef }
        width={ dimensions.width }
        height={ dimensions.height }
        onMouseDown={ checkDeselect }
        onTouchStart={ checkDeselect }
        style={ {
          backgroundColor: '#fff',
          borderRadius: '4px',
          border: '1px solid #E3E9F1',
        } }
      >
        <Layer>
          {image?.shadowSrc && mode === 'SILO' && (
            <>
              {isReady && (
                <ColorBackground
                  imageRef={ imageRef }
                  color={ color }
                  image={ image }
                />
              )}
              <ShadowImage
                ref={ shadowRef }
                stageRef={ stageRef }
                src={ image.shadowSrc }
                intensity={ intensity }
                gamma={ gamma }
                backColor={ color }
              />
            </>
          )}
          {stageRef.current && (
            <ProductImage
              ref={ imageRef }
              stageRef={ stageRef }
              src={ backgroundSrc }
              isVideo={ mode === 'VIDEO' }
              onLoad={ () => {
                setIsReady(true);
              } }
            >
              {isReady && mode === 'BBOX' && image?.bboxes?.map((bbox) => {
                const shapeProps = {
                  x: bbox.box[0],
                  y: bbox.box[1],
                  width: (bbox.box[2] - bbox.box[0]),
                  height: (bbox.box[3] - bbox.box[1]),
                };
                return (
                  <Box
                    key={ bbox.id }
                    shapeProps={ shapeProps }
                    isSelected={ bbox.id === selectedId }
                    onSelect={ () => {
                      selectShape(bbox.id);
                      image.selectedBbox = bbox.id;
                    } }
                    onChange={ (newAttrs) => {
                      const rects = rectangles.slice();
                      bbox.box = [newAttrs.x, newAttrs.y, newAttrs.x + newAttrs.width, newAttrs.y + newAttrs.height];
                      setRectangles(rects);
                    } }
                  />
                );
              })}
            </ProductImage>
          )}
        </Layer>
      </Stage>
    </div>
  );
}

export default forwardRef(ProductImageEditor);
